import { useMany, useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { Card, Table } from 'antd';
import type {
  ProfileOut,
  PublicationPremiereFollowerOut,
} from '@/api/generated/schemas';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { ProfileSelectFilter } from '@/components/account/ProfileSelectFilter';
import { orDash } from '@/components/common/show/shared';
import { useAdminTable } from '@/hooks/useAdminTable';
import { isFieldFiltered } from '@/utils/filters';
import { formatDateTime, formatThousands } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';
import { RESOURCE } from '../constants';

interface PublicationPremiereFollowersCardProps {
  publicationId: number;
  /** Total from the premiere record, shown in the card title. */
  followersCount: number;
}

/**
 * Profiles following the premiere. Rows are follow *links*, not profiles:
 * re-following inserts a new link, so one profile may appear more than once —
 * hence the link id as the row key.
 */
export const PublicationPremiereFollowersCard = ({
  publicationId,
  followersCount,
}: PublicationPremiereFollowersCardProps) => {
  const t = useTranslate();
  const f = (key: string) => t(`${RESOURCE}.followers.${key}`);

  const { tableProps, setFilters, filters, sorters } =
    useAdminTable<PublicationPremiereFollowerOut>({
      // Nested endpoint: the dot notation becomes the URL path. Unlike the
      // collections' nested routes this one has a trailing slash, so the
      // dataProvider default applies.
      resource: `${RESOURCE}.${publicationId}.followers`,
      syncWithLocation: false,
      pagination: { pageSize: 20 },
      sorters: {
        initial: [{ field: 'created_at', order: 'desc' }],
      },
    });

  const crudFilters = filters as CrudFilter[];

  // The endpoint returns bare profile ids — resolve the page in one request
  // instead of one per row.
  const profileIds = [
    ...new Set((tableProps.dataSource ?? []).map((row) => row.profile_id)),
  ];
  const { result: profilesResult } = useMany<ProfileOut>({
    resource: 'account.profiles',
    ids: profileIds,
    queryOptions: { enabled: profileIds.length > 0 },
  });
  const profileMap = new Map<number, ProfileOut>(
    (profilesResult?.data ?? []).map((p: ProfileOut) => [p.id, p])
  );

  return (
    <Card
      title={`${t(`${RESOURCE}.sections.followers`)} (${formatThousands(
        followersCount
      )})`}
    >
      <Table<PublicationPremiereFollowerOut>
        {...tableProps}
        rowKey="id"
        size="small"
        locale={{ emptyText: f('empty') }}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />
        <Table.Column
          dataIndex="profile_id"
          title={f('profile')}
          sorter
          sortOrder={getSortOrder(sorters, 'profile_id')}
          filtered={isFieldFiltered(crudFilters, 'profile_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <ProfileSelectFilter
              field="profile_id"
              placeholder={f('profile')}
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: number) => (
            <ProfileIconLink
              profileId={value}
              profile={profileMap.get(value)}
            />
          )}
        />
        <Table.Column
          dataIndex="created_at"
          title={f('followed_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          render={(value: string) => orDash(formatDateTime(value))}
        />
      </Table>
    </Card>
  );
};
